// Columbia University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Columbia's Common Data Set 2025-2026 for Columbia College and Columbia
//    Engineering, from opir.columbia.edu (C1 applicants/admits/enrollees for
//    Fall 2025 with the residency table; C8 test policy for Fall 2027; C21
//    early decision counts; G1/G5 2026-2027 costs). The file name spells
//    "Enginnering"; that is the school's own URL. The CDS covers the two
//    first-year schools together; the School of General Studies has its own.
//  - undergrad.admissions.columbia.edu (first-year applicants with dates and
//    deadlines, early decision, testing policy, Columbia-Juilliard Program,
//    cost of attendance). Columbia stays test-optional for fall 2027 entry
//    and requires the SAT or ACT from the 2027-2028 cycle (fall 2028 entry).
//  - The Class of 2030 Profile (September 2026) reports Fall 2026 totals
//    (61,031 applications, 2,585 admits); the page keeps the CDS figures,
//    which are the latest a Common Data Set reports.
//  - College Scorecard, school id 190150 (net price by family income). The
//    API was rate-limited; the figures were read from the Scorecard's own
//    school page, which shows the same latest-year fields.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Columbia Common Data Set 2025-2026 (Columbia College and Columbia Engineering)',
        url: 'https://opir.columbia.edu/sites/opir.columbia.edu/files/content/Common%20Data%20Set/2025-26_Columbia_College_and_Columbia_Enginnering_CDS.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Columbia Undergraduate Admissions, First-Year Applicants',
        url: 'https://undergrad.admissions.columbia.edu/apply/firstyear',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Columbia Undergraduate Admissions, Early Decision',
        url: 'https://undergrad.admissions.columbia.edu/apply/firstyear/early-decision',
        read: '2026-10-01',
    },
    tests: {
        label: 'Columbia Undergraduate Admissions, Testing Policy',
        url: 'https://undergrad.admissions.columbia.edu/apply/process/testing',
        read: '2026-10-01',
    },
    juilliard: {
        label: 'Columbia Undergraduate Admissions, The Columbia-Juilliard Program',
        url: 'https://undergrad.admissions.columbia.edu/apply/firstyear/columbia-juilliard',
        read: '2026-10-01',
    },
    cost: {
        label: 'Columbia Undergraduate Admissions, Cost and Aid',
        url: 'https://undergrad.admissions.columbia.edu/affordability/cost',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190150-Columbia-University-in-the-City-of-New-York',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'columbia-university',
    kbId: 'columbia_university',
    name: 'Columbia University',
    shortName: 'Columbia',
    aliases: ['Columbia College', 'Columbia Engineering'],
    location: 'New York, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Columbia visual identity); decoration only.
    branding: {
        primary: '#B9D9EB',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/columbia_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 59611,
        admitted: 2946,
        enrolled: 1797,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 8882, admitted: 447 },
            { group: 'Out-of-state', applied: 35349, admitted: 2092 },
            { group: 'International', applied: 15380, admitted: 407 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Columbia\'s Common Data Set reports one first-year acceptance rate for Columbia College and Columbia Engineering together, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        rows: [
            { plan: 'Early Decision', applied: 5868, admitted: 819 },
        ],
        note: 'Early decision counts for the Fall 2025 entering class, as the Common Data Set reports them. They are part of the totals above. Columbia has no early action plan.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Columbia College or Columbia Engineering with one application. A few choices ask for more:',
        rows: [
            { school: 'Columbia Engineering', requirement: 'Two teacher recommendations, and one of them must come from a math or science teacher.', source: 'firstYear' },
            { school: 'Columbia-Juilliard Program (Columbia College)', requirement: 'The Barnard-Columbia-Juilliard portion of Juilliard\'s application as well, due December 1. About 15 students are admitted each year.', source: 'juilliard' },
        ],
        note: 'Supplementary materials such as an arts portfolio or a research abstract are optional for everyone else.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Late March', binding: false },
        ],
        note: 'The QuestBridge National College Match deadline is also November 1. Financial aid applications are due November 15 for Early Decision and February 15 for Regular Decision. Admitted Regular Decision students reply by May 1. Recommendations and school reports are accepted after the deadline.',
        source: 'firstYear',
        binding: 'Early decision is binding: if you are admitted, you must accept Columbia\'s offer and withdraw all your other applications. Columbia releases you only if, after talking with its financial aid office, you cite financial reasons for not attending. If you are denied in early decision, you cannot apply to Columbia again that year.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Columbia is test-optional for students applying in the 2026-2027 cycle to enter in fall 2027. It is not test-blind: you choose in the application whether your SAT or ACT scores are considered. You may test as late as November 2026 for Early Decision and February 2027 for Regular Decision.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [
            { topic: 'Testing', line: 'Columbia requires the SAT or ACT for fall 2028 entry, starting with the 2027-2028 cycle in August 2027. Applicants who face challenges meeting the requirement may ask for a waiver.', source: 'tests' },
        ],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 72800 },
            { item: 'Fees', amount: 4160 },
            { item: 'Food', amount: 7128 },
            { item: 'Housing (on campus)', amount: 12522 },
            { item: 'Books and supplies', amount: 1320 },
            { item: 'Personal expenses', amount: 1844 },
        ],
        total: 99774,
        note: 'For a first-year student; first-years live on campus. Fees include one-time transcript and orientation fees of $730. Travel and local transportation varies with how far you live from New York and is not in the total.',
        source: 'cost',
        where: '2026-2027 Cost of Attendance',
        netPrice: {
            average: 15370,
            byIncome: [
                { income: '$0 to $30,000', amount: 2275 },
                { income: '$30,001 to $48,000', amount: 4020 },
                { income: '$48,001 to $75,000', amount: 4878 },
                { income: '$75,001 to $110,000', amount: 8881 },
                { income: 'Over $110,000', amount: 40931 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Columbia\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
